import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import OptionalComponent from '@ui/optional-component/optional-component';
import Button from '@ui/buttons/button/button';
import { mergeRefs } from '@/utils/merge-refs';
import { isDefined } from '@/utils/is-defined';

import styles from './search-input.module.css';

type SearchInputProps = {
  isClearable?: boolean;
  placeholder?: string;
  placeholderColor?: string;
  searchParamName?: string;
  /**
   * just set pageParamName to a random value if you don't want 'page' to change when searching
   * because maybe you've got multiple search components and you're using page for something else
   * */
  pageParamName?: string;
  loading?: boolean;
  height?: string;
  width?: string;
  refocusAfterClear?: boolean;
  onSubmit?: (search: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onClear?: () => void;
  trackInParams?: boolean;
  debounceTime?: number;
  expandOnFocus?: boolean;
  submitOnClear?: boolean;
  wrapperClass?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  alwaysShowCloseIcon?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  isBorderless?: boolean;
  border?: CSSProperties['border'];
  svgColor?: string;
  clearIconLayout?: {
    colors?: {
      background?: string;
      hoverBackground?: string;
      svg?: string;
    };
  };
  submitOnDebounce?: boolean;
  inputStyle?: CSSProperties;
};

const SearchInput = ({
  isClearable = true,
  pageParamName = 'page',
  searchParamName = 'search',
  placeholder = 'Search...',
  placeholderColor,
  loading,
  height = '32px',
  width = '100%',
  refocusAfterClear = true,
  onSubmit,
  onBlur,
  onFocus,
  onClear,
  trackInParams = false,
  debounceTime = 300,
  expandOnFocus,
  submitOnClear,
  wrapperClass,
  inputProps = {},
  alwaysShowCloseIcon,
  inputRef,
  isBorderless = false,
  border,
  svgColor,
  clearIconLayout,
  submitOnDebounce = true,
  inputStyle = {},
}: SearchInputProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const _inputRef = useRef<HTMLInputElement>(null);

  const defaultValue = useMemo(() => {
    if (!trackInParams) return '';
    const _searchParams = new URLSearchParams(searchParams);
    const search = _searchParams.get(searchParamName);
    return decodeURIComponent(search || '');
  }, [trackInParams, searchParams, searchParamName]);

  const [hasText, setHasText] = useState(!!defaultValue.length);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const inputEl = _inputRef.current;
    if (!inputEl) return;

    if (!defaultValue.trim().length) {
      inputEl.value = '';
    }
  }, [defaultValue, _inputRef]);

  useEffect(() => {
    if (trackInParams && decodeURIComponent(searchParams.get(searchParamName) || '') === '') {
      // clear state
      setHasText(false);
      // clear input ref value
      if (inputRef?.current) {
        inputRef.current.value = '';
      }
    }
  }, [trackInParams, searchParams, searchParamName, inputRef]);

  const submitAction = useCallback(
    (e: SubmitEvent) => {
      e.preventDefault();
      if (!(e.target instanceof HTMLFormElement)) return;
      const formData = new FormData(e.target as HTMLFormElement);
      const userInput = (formData.get(searchParamName) || '').toString().trim();

      if (trackInParams) {
        // get existing search params, but reaplace the search param with the new value
        const newParams = new URLSearchParams(searchParams);
        if (userInput.length) {
          newParams.set(searchParamName, encodeURIComponent(userInput));
        } else {
          newParams.delete(searchParamName);
        }
        newParams.delete(pageParamName); // always reset to page 1 when searching
        setSearchParams(newParams);
      }
      onSubmit?.(userInput);
    },
    [onSubmit, pageParamName, searchParamName, searchParams, setSearchParams, trackInParams]
  );

  const handleDebounce = () => {
    if (!submitOnDebounce) return;
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handleClear = () => {
    // clear state
    setHasText(false);
    // clear input ref value
    if (_inputRef?.current) {
      _inputRef.current.value = '';
    }
    // delete search param
    if (trackInParams) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(searchParamName);
      setSearchParams(newParams);
    }

    if (refocusAfterClear) {
      _inputRef.current?.focus();
    }
    onClear?.();

    if (submitOnClear) {
      onSubmit?.('');
    }
  };

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    // use event listener instead of jsx onSubmit to be sure to catch all submit actions
    form.addEventListener('submit', submitAction);

    return () => {
      form.removeEventListener('submit', submitAction);
    };
  }, [submitAction]);

  return (
    <form
      className={`${styles.form} ${expandOnFocus ? styles.expandOnFocus : ''} ${hasText ? styles.hasText : ''}`}
      ref={formRef}
    >
      <InputWithOverlay
        enableChildrenPointerEvents={false}
        inputHeight={height}
        inputWidth={width}
        inputProps={{
          type: 'text',
          name: searchParamName,
          defaultValue,
          placeholder,
          className: `${styles.searchInput} ${isClearable ? styles.isClearable : ''}${isBorderless ? ' borderless' : ''}`,
          style: {
            ...(isDefined(placeholderColor) ? { '--search-input-placholder-color': placeholderColor } : {}),
            ...inputStyle,
          } as CSSProperties,
          onChange: (e) => setHasText(!!e.target.value),
          onFocus: () => {
            onFocus?.();
            setIsFocused(true);
          },
          onBlur: () => {
            onBlur?.();
            setIsFocused(false);
          },
          ...inputProps,
        }}
        wrapperClass={wrapperClass}
        inputRef={mergeRefs(_inputRef, inputRef)}
        border={border || undefined}
        onDebounce={handleDebounce}
        debounceTime={debounceTime}
        renderInnerContent={() => {
          return (
            <div
              className={classNames('flex-column jc-center ai-center', styles.searchIconContainer, {
                'padding-l-16': !expandOnFocus || (expandOnFocus && (isFocused || hasText)),
                'padding-h-8': expandOnFocus && !(isFocused || hasText),
              })}
            >
              <Icon
                name={ICONS.SEARCH_ROUND}
                size={!expandOnFocus || (expandOnFocus && (isFocused || hasText)) ? 12 : 16}
                color={svgColor}
              />
            </div>
          );
        }}
        innerContentStyle={{ left: 0 }}
        isBorderless={isBorderless}
      >
        <OptionalComponent display={alwaysShowCloseIcon || !!(isClearable && hasText && !loading)}>
          <div className="position-absolute top-0 right-0 margin-r-6 flex-column jc-center ai-center height-100-percent justify-center">
            <Button
              hoverBackgroundColor={clearIconLayout?.colors?.hoverBackground ?? 'var(--gray-600)'}
              backgroundColor={clearIconLayout?.colors?.background}
              type="button"
              onClick={handleClear}
              className="clear-button padding-4 radius-full"
            >
              <Icon name={ICONS.CLOSE} size={12} color={clearIconLayout?.colors?.svg} />
            </Button>
          </div>
        </OptionalComponent>
        <OptionalComponent display={isFocused && loading}>
          <div className="position-absolute top-0 right-0 margin-r-20 flex-column jc-center ai-center height-100-percent justify-center">
            <WaitingIndicator />
          </div>
        </OptionalComponent>
      </InputWithOverlay>
    </form>
  );
};

export default SearchInput;
